/**
 * The sidebar's navigation as data (SA-960, spec
 * docs/superpowers/specs/2026-10-06-sidebar-navigation-design.md §1): every
 * row, its group and its gate, from the features, the permissions and the
 * record noun. Pure: lucide icons are its only import; no React, no store, no
 * hook, and nothing from `@/custom`, whose importers are pinned, so surfaces
 * are typed structurally and passed in. `useNavModel` builds the input;
 * `Sidebar` and `AdminArea` render the result and decide no visibility of
 * their own.
 */
import type { LucideIcon } from 'lucide-react'
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  ClipboardList,
  Building2,
  Code2,
  Cog,
  Contact,
  Cpu,
  FileImage,
  FileText,
  FlaskConical,
  Home,
  Inbox,
  Key,
  KeyRound,
  LineChart,
  ListChecks,
  Mail,
  MessageSquare,
  MessageSquareHeart,
  Phone,
  Plane,
  Plug,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Store,
  Users,
  Workflow,
  Wrench,
} from 'lucide-react'

/** A custom surface as the model reads it; the manifest's `SurfaceEntry` fits structurally. */
export interface NavSurface {
  id: string
  route: string
  feature: string
  permission: string
  parent?: string
  nav?: { section: 'primary' | 'admin'; label: string; icon: LucideIcon; tag?: string }
}

export interface NavInput {
  /** The feature store's `isEnabled`: true until the manifest loads. */
  has: (feature: string) => boolean
  /** `has(feature)` and the permission store's `hasPermission(permission)`. */
  can: (feature: string, permission: string) => boolean
  /** `hasPermission('admin:access')`. */
  isAdmin: boolean
  /** The permission store's `hasPermission`. Optional: absent, it reads `isAdmin` (a caller or a
   * test that predates it). AI models gates on `ai_models:read` (SA-1103). */
  hasPermission?: (permission: string) => boolean
  /** The loaded manifest's `companies_page` (SA-1456 spec §7.2): a module uses companies. Absent = false. */
  companiesPage?: boolean
  recordNoun: { singular: string; plural: string }
  surfaces: readonly NavSurface[]
  badges: { inboxUnread: number; inboxEscalated: number; ordersNew: number }
}

export interface NavRow {
  /** A stable slug; tests key on it, never on the label. A surface row's is its manifest id. */
  id: string
  to: string
  label: string
  icon: LucideIcon
  badge?: number
  escalated?: number
  /** A short word beside the label, a surface's `nav.tag` (SA-1097). */
  tag?: string
  /** NavLink's exact match. Only Home sets it (spec §1.5). */
  end?: boolean
}

export interface NavGroup {
  id: string
  label: string | null
  rows: NavRow[]
}

export interface NavModel {
  sidebar: NavGroup[]
  /** The admin area's sub-navigation (SA-963, spec §4.3); empty groups dropped. */
  adminArea: NavGroup[]
}

/** The rows whose gate holds, in the order written. */
function gated(entries: ReadonlyArray<readonly [boolean, NavRow]>): NavRow[] {
  return entries.filter(([on]) => on).map(([, row]) => row)
}

/** A section's surfaces, in manifest order (spec §1.4); `nav.order` stays unread. */
function surfaceRows(input: NavInput, section: 'primary' | 'admin'): NavRow[] {
  return input.surfaces.flatMap((s) => {
    const nav = s.nav
    if (!nav || nav.section !== section || !input.can(s.feature, s.permission)) return []
    return [{ id: s.id, to: s.route, label: nav.label, icon: nav.icon, ...(nav.tag ? { tag: nav.tag } : {}) }]
  })
}

export function buildNav(input: NavInput): NavModel {
  const { has, can, badges } = input
  const inbox = can('inbox', 'inbox:read')

  const primary = [
    ...gated([
      [true, { id: 'home', to: '/', label: 'Home', icon: Home, end: true }],
      [inbox, {
        id: 'inbox', to: '/inbox', label: 'Inbox', icon: Inbox,
        badge: badges.inboxUnread, escalated: badges.inboxEscalated,
      }],
      // E4 (SA-389, spec D26): the record page rides workflow_engine under the
      // deployment's noun; commerce only picks the icon.
      [can('workflow_engine', 'inbox:read'), {
        id: 'records', to: '/orders', label: input.recordNoun.plural,
        icon: has('commerce') ? ShoppingCart : ClipboardList, badge: badges.ordersNew,
      }],
      [can('process_engine', 'tasks:read'), { id: 'tasks', to: '/tasks', label: 'Tasks', icon: ListChecks }],
      [has('marketing'), { id: 'marketing', to: '/marketing', label: 'Marketing', icon: BarChart3 }],
      [has('crm'), { id: 'crm', to: '/crm', label: 'CRM', icon: Contact }],
    ]),
    ...surfaceRows(input, 'primary'),
    // Core and shown to everyone, last (SA-961, spec §2.1): the New
    // conversation button and the recent list live on the page it opens.
    { id: 'chat', to: '/conversations', label: 'Chat', icon: MessageSquare },
  ]

  // The authoring surfaces (SA-962, spec §1.6, §3.1). Assets is the approved
  // asset library personas use, so it sits here, behind the Inbox's gate as
  // before. Your settings and Help live in the user menu at the sidebar foot.
  const build = gated([
    [true, { id: 'personas', to: '/personas', label: 'Personas', icon: Users }],
    [inbox, { id: 'assets', to: '/assets', label: 'Assets', icon: FileImage }],
    [can('workflow_transparency', 'workflows:read'), { id: 'workflows', to: '/workflows', label: 'Workflows', icon: Workflow }],
    [can('replay_lab', 'workflows:configure'), { id: 'replay-lab', to: '/replay-lab', label: 'Replay Lab', icon: FlaskConical }],
  ])

  // The admin area (SA-963, spec §4.3). A core row needs the admin and its own
  // gate, today's; a module's admin surface needs only its own can(), so a
  // custom role holding its permission finds it here by its URL (spec §4.1).
  const isAdmin = input.isAdmin
  const perm = input.hasPermission ?? (() => input.isAdmin)
  const adminArea: NavGroup[] = [
    {
      id: 'access', label: 'Access', rows: gated([
        [isAdmin, { id: 'users', to: '/admin/users', label: 'Users', icon: Shield }],
        [isAdmin, { id: 'service-accounts', to: '/admin/service-accounts', label: 'Service accounts', icon: KeyRound }],
        [isAdmin && has('rbac'), { id: 'roles', to: '/admin/roles', label: 'Roles', icon: ShieldCheck }],
        [isAdmin, { id: 'sharing', to: '/admin/sharing', label: 'Sharing', icon: Share2 }],
      ]),
    },
    {
      id: 'channels', label: 'Channels', rows: gated([
        [isAdmin && has('email_ingestion'), { id: 'mailboxes', to: '/admin/mailboxes', label: 'Mailboxes', icon: Mail }],
        [isAdmin && has('whatsapp'), { id: 'whatsapp', to: '/admin/whatsapp', label: 'WhatsApp', icon: Phone }],
        [isAdmin && has('widget'), { id: 'widgets', to: '/admin/widgets', label: 'Widgets', icon: Code2 }],
      ]),
    },
    {
      // Credential sets gains a row (spec §4.3); the hub's header link stays.
      id: 'integrations', label: 'Integrations', rows: gated([
        [isAdmin, { id: 'integrations', to: '/admin/integrations', label: 'Integrations', icon: Plug }],
        [isAdmin && has('tool_framework'), { id: 'tools', to: '/admin/tools', label: 'Tools', icon: Wrench }],
        [isAdmin, { id: 'credential-sets', to: '/admin/credential-sets', label: 'Credential sets', icon: Key }],
      ]),
    },
    {
      id: 'settings', label: 'Settings', rows: [
        ...gated([
          [isAdmin, { id: 'app-settings', to: '/admin/app-settings', label: 'App settings', icon: Cog }],
          [perm('ai_models:read'), { id: 'ai-models', to: '/admin/ai-models', label: 'AI models', icon: Cpu }],
          // SA-1366: the airports reference is the aviation kit's.
          [isAdmin && has('aviation'), { id: 'airports', to: '/admin/airports', label: 'Airports', icon: Plane }],
          // SA-1456 spec §7.2: a manager's page, offered only where a module uses companies.
          [perm('companies:manage') && input.companiesPage === true, {
            id: 'companies', to: '/admin/companies', label: 'Companies', icon: Building2,
          }],
          [isAdmin && has('workflow_engine'), { id: 'quote-document', to: '/admin/quote-document', label: 'Quote document', icon: FileText }],
          [perm('orders:settings:manage') && has('process_engine'), {
            id: 'record-settings', to: '/admin/record-settings', label: `${input.recordNoun.singular} settings`, icon: SlidersHorizontal,
          }],
          [isAdmin && has('commerce'), { id: 'business', to: '/admin/business', label: 'Business', icon: Store }],
          [isAdmin && has('commerce'), { id: 'products', to: '/admin/products', label: 'Products', icon: ShoppingCart }],
          [isAdmin && has('voice'), { id: 'pronunciation', to: '/admin/pronunciation-dictionaries', label: 'Pronunciation', icon: BookOpen }],
          [isAdmin && has('portability'), { id: 'portability', to: '/admin/portability', label: 'Portability', icon: ArrowLeftRight }],
        ]),
        ...surfaceRows(input, 'admin'),
      ],
    },
    {
      id: 'analysis', label: 'Analysis', rows: gated([
        [isAdmin && can('conversation_analytics', 'conversation_analysis:read'), {
          id: 'conversation-analysis', to: '/admin/conversation-analysis', label: 'Conversation analysis', icon: LineChart,
        }],
        [has('product_feedback') && perm('app_feedback:read'), {
          id: 'feedback', to: '/admin/feedback', label: 'Product feedback', icon: MessageSquareHeart,
        }],
      ]),
    },
  ]

  // A group with no rows is dropped (spec §1.3, §4.3); the components render what they are given.
  const nonEmpty = (gs: NavGroup[]) => gs.filter((g) => g.rows.length > 0)
  const visibleArea = nonEmpty(adminArea)

  // The sidebar's one Admin row (SA-963, spec §1.6, §4.5) opens the admin area;
  // it shows whenever the area holds a row for this viewer (SA-1115), so a custom
  // role holding one admin page's permission finds the area from the sidebar.
  // It is lit on every /admin/* page by NavLink's prefix match.
  const groups: NavGroup[] = [
    { id: 'primary', label: null, rows: primary },
    { id: 'build', label: 'Build', rows: build },
    ...(visibleArea.length > 0
      ? [{ id: 'admin', label: null, rows: [{ id: 'admin', to: '/admin', label: 'Admin', icon: Shield }] }]
      : []),
  ]

  return { sidebar: nonEmpty(groups), adminArea: visibleArea }
}
